import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Card, Avatar, 
    MenuItem, Select, FormControl, InputLabel,
    CircularProgress, TextField, Stack, IconButton, Tooltip,
    Fade
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    FileDownload as FileDownloadIcon,
    Event as EventIcon,
    People as PeopleIcon,
    Search as SearchIcon,
    Group as GroupIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Notifications as NotificationIcon,
    EventBusy as EventBusyIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getAllSimpleStaffs } from '../../../redux/staffRelated/staffHandle';
import { exportToExcel } from '../../../utils/excelExport';
import { useToast } from '../../../context/ToastContext';
import { 
    CustomNepaliDatePicker, 
    getNepaliMonthOptions, 
    getNepaliYearOptions, 
    formatDatePart, 
    getCurrentBSDate 
} from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f0f4f8;
    min-height: 100vh;
    padding-bottom: 60px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 80px 0 120px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -80px;
    position: relative;
    z-index: 2;
`;

const HeroCard = styled(Paper)`
    border-radius: 32px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    padding: 40px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1) !important;
    margin-bottom: 40px;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
    overflow: hidden;
    background: white !important;
`;

const TableWrapper = styled(Paper)`
    border-radius: 32px !important;
    overflow: hidden;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05) !important;
`;

const StaffRow = styled(TableRow)`
    &:hover {
        background-color: #f8fafc;
    }
    
    td {
        padding: 16px 20px;
        border-bottom: 1px solid #f1f5f9;
    }
`;

const StaffAvatar = styled(Avatar)`
    width: 56px !important;
    height: 56px !important;
    border-radius: 18px !important;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
    box-shadow: 0 8px 16px rgba(99, 102, 241, 0.2) !important;
`;

const StatusBadge = styled(Chip)`
    height: 32px !important;
    border-radius: 10px !important;
    font-weight: 800 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: none !important;
`;

const ActionBtn = styled(Button)`
    border-radius: 14px !important;
    padding: 8px 16px !important;
    font-weight: 800 !important;
    text-transform: none !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    
    &:active {
        transform: scale(0.95);
    }
`;

const EditBtn = styled(IconButton)`
    background: #f1f5f9 !important;
    border-radius: 12px !important;
    transition: all 0.2s !important;
    
    &:hover {
        background: #e2e8f0 !important;
        color: #6366f1 !important;
        transform: rotate(15deg);
    }
`;



const StaffAttendance = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const { staffList, loading: reduxLoading } = useSelector((state) => state.staff);
    const { showToast } = useToast();

    const [staff, setStaff] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getCurrentBSDate());
    const [savingId, setSavingId] = useState(null);
    const [editingStaffId, setEditingStaffId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedMonth, setSelectedMonth] = useState("01");
    const [selectedYear, setSelectedYear] = useState(2081);
    const [exporting, setExporting] = useState(false);

    const schoolId = currentUser?._id;
    const years = getNepaliYearOptions();
    const months = getNepaliMonthOptions();

    useEffect(() => {
        if (schoolId) dispatch(getAllSimpleStaffs(schoolId));
    }, [schoolId, dispatch]);

    useEffect(() => {
        if (staffList && Array.isArray(staffList)) {
            setStaff(staffList);
        }
    }, [staffList]);

    const attendanceMap = useMemo(() => {
        const map = {};
        staff.forEach(s => {
            if (s.attendance) {
                const record = s.attendance.find(a => 
                    formatDatePart(a.date) === selectedDate
                );
                if (record) {
                    map[s._id] = { status: record.status };
                }
            }
        });
        return map;
    }, [staff, selectedDate]);

    const handleAttendanceChange = async (staffId, status) => {
        const currentStatus = attendanceMap[staffId]?.status;
        if (currentStatus === status) {
            setEditingStaffId(null);
            return;
        }

        try {
            setSavingId(staffId);
            
            // Optimistic UI update
            setStaff(prev => prev.map(s => {
                if (s._id === staffId) {
                    const newAttendance = [...(s.attendance || [])];
                    const existingIndex = newAttendance.findIndex(a => formatDatePart(a.date) === selectedDate);
                    const record = { date: selectedDate, status };
                    
                    if (existingIndex > -1) newAttendance[existingIndex] = { ...newAttendance[existingIndex], ...record };
                    else newAttendance.push(record);
                    
                    return { ...s, attendance: newAttendance };
                }
                return s;
            }));

            await axios.post(`${process.env.REACT_APP_BASE_URL}/StaffAttendance/${staffId}`, {
                status,
                date: selectedDate
            });

            setEditingStaffId(null);
            showToast(`${status} marked for ${staff.find(s => s._id === staffId)?.name}`, 'success');
            dispatch(getAllSimpleStaffs(schoolId));
        } catch (error) {
            console.error('Attendance error:', error);
            showToast('Failed to save attendance', 'error');
        } finally {
            setSavingId(null);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            let url = `${process.env.REACT_APP_BASE_URL}/Staffs/AttendanceExport/${schoolId}?month=${selectedMonth}&year=${selectedYear}`;

            const response = await axios.get(url);
            if (response.data?.data?.length) {
                const monthLabel = months.find(m => m.value === selectedMonth)?.label || selectedMonth;
                exportToExcel(response.data.data, `Staff_Attendance_${monthLabel}_${selectedYear}`, 'Staff Attendance');
                showToast('Attendance report exported to Excel', 'success');
            } else {
                showToast(`No attendance found for ${selectedMonth}`, 'info');
            }
        } catch (error) {
            showToast('Failed to generate report', 'error');
        } finally {
            setExporting(false);
        }
    };

    const filteredStaff = staff.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.position && s.position.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const presentCount = Object.values(attendanceMap).filter(a => a.status === 'Present').length;
    const absentCount = Object.values(attendanceMap).filter(a => a.status === 'Absent').length;
    const leaveCount = Object.values(attendanceMap).filter(a => a.status === 'Leave').length;
    const notMarkedCount = staff.length - presentCount - absentCount - leaveCount;

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                <Tooltip title="Back">
                                    <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                                        <ArrowBackIcon />  
                                    </IconButton>
                                </Tooltip>
                                <Typography variant="h2" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.03em' }}>
                                    Staff Registry
                                </Typography>
                            </Stack>
                            <Typography variant="h6" sx={{ opacity: 0.6, maxWidth: 600, fontWeight: 400 }}>
                                Professional real-time workforce management and attendance tracking.
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ mt: 2 }}>
                <HeroCard>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EventIcon color="primary" /> Select Active Date
                            </Typography>
                            <CustomNepaliDatePicker
                                label="Active Date"
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={3}>
                                {[
                                    { label: 'Total Personnel', value: staff.length, icon: <PeopleIcon />, color: '#6366f1' },
                                    { label: 'Present', value: presentCount, icon: <CheckCircleIcon />, color: '#10b981' },
                                    { label: 'Absent', value: absentCount, icon: <CancelIcon />, color: '#f43f5e' },
                                    { label: 'On Leave', value: leaveCount, icon: <EventBusyIcon />, color: '#f59e0b' }
                                ].map((stat, i) => (
                                    <Grid item xs={6} sm={3} key={i}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: stat.color }}>{stat.value}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{stat.label}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </HeroCard>

                <Grid container spacing={4}>
                    <Grid item xs={12} lg={9}>
                        <TableWrapper>
                           
                            <TableContainer>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3 }}>STAFF MEMBER</TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: '#475569' }}>ROLE & DEPT</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800, color: '#475569' }}>STATUS</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: '#475569' }}>ACTION</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reduxLoading && staff.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                                    <CircularProgress size={60} thickness={2} />
                                                    <Typography sx={{ mt: 2, fontWeight: 600, color: '#94a3b8' }}>Syncing with Database...</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredStaff.map((s, idx) => {
                                            const status = attendanceMap[s._id]?.status || 'Not Marked';
                                            const isMarked = status === 'Present' || status === 'Absent' || status === 'Leave';
                                            const isEditing = editingStaffId === s._id;
                                            const isSaving = savingId === s._id;

                                            return (
                                                <StaffRow key={s._id}>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <StaffAvatar src={s.photo ? `${process.env.REACT_APP_BASE_URL}/${s.photo}` : ''}>
                                                                {s.name.charAt(0)}
                                                            </StaffAvatar>
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 900, color: '#1e293b' }}>{s.name}</Typography>
                                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <PhoneIcon sx={{ fontSize: 12 }} /> {s.phone || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>{s.position || 'Staff'}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <BusinessIcon sx={{ fontSize: 12 }} /> {s.department || 'Admin'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <StatusBadge
                                                            label={status}
                                                            sx={{
                                                                bgcolor: status === 'Present' ? '#dcfce7' : status === 'Absent' ? '#fee2e2' : status === 'Leave' ? '#fef3c7' : '#f1f5f9',
                                                                color: status === 'Present' ? '#15803d' : status === 'Absent' ? '#b91c1c' : status === 'Leave' ? '#92400e' : '#475569'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {isMarked && !isEditing ? (
                                                            <EditBtn onClick={() => setEditingStaffId(s._id)}>
                                                                <EditIcon fontSize="small" />
                                                            </EditBtn>
                                                        ) : (
                                                            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                                                <ActionBtn
                                                                    variant={status === 'Present' ? 'contained' : 'outlined'}
                                                                    color="success"
                                                                    onClick={() => handleAttendanceChange(s._id, 'Present')}
                                                                    disabled={isSaving}
                                                                    startIcon={status === 'Present' ? <CheckCircleIcon /> : null}
                                                                    sx={{ bgcolor: status === 'Present' ? '#10b981' : 'transparent' }}
                                                                >
                                                                    {isSaving && savingId === s._id ? 'Saving...' : 'Present'}
                                                                </ActionBtn>
                                                                <ActionBtn
                                                                    variant={status === 'Absent' ? 'contained' : 'outlined'}
                                                                    color="error"
                                                                    onClick={() => handleAttendanceChange(s._id, 'Absent')}
                                                                    disabled={isSaving}
                                                                    startIcon={status === 'Absent' ? <CancelIcon /> : null}
                                                                    sx={{ bgcolor: status === 'Absent' ? '#f43f5e' : 'transparent' }}
                                                                >
                                                                    Absent
                                                                </ActionBtn>
                                                                <ActionBtn
                                                                    variant={status === 'Leave' ? 'contained' : 'outlined'}
                                                                    color="warning"
                                                                    onClick={() => handleAttendanceChange(s._id, 'Leave')}
                                                                    disabled={isSaving}
                                                                    startIcon={status === 'Leave' ? <EventBusyIcon /> : null}
                                                                    sx={{ bgcolor: status === 'Leave' ? '#f59e0b' : 'transparent', color: status === 'Leave' ? 'white' : '#f59e0b', borderColor: '#f59e0b' }}
                                                                >
                                                                    Leave
                                                                </ActionBtn>
                                                                {isEditing && (
                                                                    <IconButton onClick={() => setEditingStaffId(null)} sx={{ color: '#94a3b8' }}>
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                </StaffRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </TableWrapper>
                    </Grid>
                    
                    <Grid item xs={12} lg={3}>
                        <Stack spacing={4}>
                            <PremiumCard sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, mb: 3 }}>Export Options</Typography>
                                <Stack spacing={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Month</InputLabel>
                                        <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} label="Month" sx={{ borderRadius: '14px' }}>
                                            {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <InputLabel>Year</InputLabel>
                                        <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year" sx={{ borderRadius: '14px' }}>
                                            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <ActionBtn
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        startIcon={<FileDownloadIcon />}
                                        onClick={handleExport}
                                        disabled={exporting}
                                        sx={{ py: 2, bgcolor: '#0f172a' }}
                                    >
                                        {exporting ? 'Exporting...' : 'Export Attendance'}
                                    </ActionBtn>
                                </Stack>
                            </PremiumCard>
                            
                            <Box sx={{ p: 3, borderRadius: '24px', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <NotificationIcon />
                                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8 }}>SYSTEM TIP</Typography>
                                </Stack>
                                <Typography sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                                    Attendance data is automatically synchronized across all admin devices in real-time.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default StaffAttendance;
